import type { DayMode, FilterableListing, FilterState, StatusView } from '../../types';
import { sortDays } from './days';
import { normaliseCategoryId } from './toFilterableListing';

// The names flyers and shared links already carry; see the plan's "Flyer-URL baseline".
export const FILTER_PARAMS = {
  view: 'view',
  page: 'page',
  days: 'pub_daysAvailable',
  category: 'category',
  location: 'pub_shortLocation',
  search: 'q',
} as const;

const HAS_ANY_PREFIX = 'has_any:';
// Sharetribe's default when no prefix is given; the UI never writes it.
const HAS_ALL_PREFIX = 'has_all:';

export const DEFAULT_FILTER_STATE: FilterState = {
  view: 'active',
  days: [],
  dayMode: 'any',
  categories: [],
  locations: [],
};

// As the list route: anything but `active` is the Inactive tab.
const parseView = (value: string | null): StatusView =>
  value === null || value === 'active' ? 'active' : 'inactive';

const parsePage = (value: string | null) => {
  const page = Number(value);

  return Number.isInteger(page) && page >= 1 ? page : 1;
};

const parseDays = (value: string | null): { days: FilterState['days']; dayMode: DayMode } => {
  if (!value) return { days: [], dayMode: 'any' };

  const isAny = value.startsWith(HAS_ANY_PREFIX);
  const isAll = value.startsWith(HAS_ALL_PREFIX);
  let list = value;
  if (isAny) list = value.slice(HAS_ANY_PREFIX.length);
  if (isAll) list = value.slice(HAS_ALL_PREFIX.length);
  const days = sortDays(list.split(',').map((day) => day.trim().toLowerCase()));

  // One day means the same either way, so settle on the mode the UI writes.
  return { days, dayMode: isAny || days.length <= 1 ? 'any' : 'all' };
};

const parseCategories = (value: string | null) => [
  ...new Set((value ?? '').split(',').map(normaliseCategoryId).filter(Boolean)),
];

// Building names often contain commas, so each location gets its own param. Old shared
// links put several in one comma-joined value, which only a known name keeps whole.
const parseLocations = (params: URLSearchParams, knownLocations: readonly string[]) => {
  const values = params.getAll(FILTER_PARAMS.location).filter(Boolean);

  if (values.length === 1 && !knownLocations.includes(values[0])) {
    return values[0]
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  return values;
};

export interface ParseFilterUrlOptions {
  /** Every listing's location display name, for telling a comma in a name from the old joined form. */
  knownLocations: readonly string[];
}

export const parseFilterUrl = (
  params: URLSearchParams,
  { knownLocations }: ParseFilterUrlOptions
): { filter: FilterState; page: number } => ({
  filter: {
    view: parseView(params.get(FILTER_PARAMS.view)),
    ...parseDays(params.get(FILTER_PARAMS.days)),
    categories: parseCategories(params.get(FILTER_PARAMS.category)),
    locations: parseLocations(params, knownLocations),
  },
  page: parsePage(params.get(FILTER_PARAMS.page)),
});

// Every listing's location, not just the shown ones: it decides whether a comma in an
// old link's `pub_shortLocation` is part of a name or joins two.
export const parseFilterUrlForRows = (
  params: URLSearchParams,
  rows: readonly Pick<FilterableListing, 'location'>[]
) => {
  const knownLocations = rows.map((row) => row.location).filter((name): name is string => !!name);

  return parseFilterUrl(params, { knownLocations });
};

/**
 * For a filter change: writes `filter` over `params`, drops `page` (back to 1) and
 * keeps every unrelated param. Defaults are left out of the URL.
 */
export const writeFilterUrl = (params: URLSearchParams, filter: FilterState): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(FILTER_PARAMS.page);

  if (filter.view === 'active') {
    next.delete(FILTER_PARAMS.view);
  } else {
    next.set(FILTER_PARAMS.view, filter.view);
  }

  const days = sortDays(filter.days);
  if (days.length === 0) {
    next.delete(FILTER_PARAMS.days);
  } else {
    const isAll = filter.dayMode === 'all' && days.length > 1;
    next.set(FILTER_PARAMS.days, `${isAll ? '' : HAS_ANY_PREFIX}${days.join(',')}`);
  }

  const categories = parseCategories(filter.categories.join(','));
  if (categories.length === 0) {
    next.delete(FILTER_PARAMS.category);
  } else {
    next.set(FILTER_PARAMS.category, categories.join(','));
  }

  next.delete(FILTER_PARAMS.location);
  filter.locations.forEach((location) => next.append(FILTER_PARAMS.location, location));

  return next;
};

export const parseSearch = (params: URLSearchParams) => params.get(FILTER_PARAMS.search) ?? '';

// Kept as typed, trailing space and all, since the field reads its value back from here.
export const writeSearch = (params: URLSearchParams, query: string): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(FILTER_PARAMS.page);

  if (query === '') {
    next.delete(FILTER_PARAMS.search);
  } else {
    next.set(FILTER_PARAMS.search, query);
  }

  return next;
};

export const writePage = (params: URLSearchParams, page: number): URLSearchParams => {
  const next = new URLSearchParams(params);

  if (page <= 1) {
    next.delete(FILTER_PARAMS.page);
  } else {
    next.set(FILTER_PARAMS.page, String(page));
  }

  return next;
};
